import { RouteLocationNormalized, type RouteLocationRaw } from "vue-router";
import storageService from "@/core/services/storage.service";
import { STORAGE_KEYS } from "@/core/constants/storageKeys";
import { PATH } from "@/core/constants/path";
import { PERMISSIONS } from "@/core/enums/PERMISSIONS";
import { isRoleAllowed, isUserPermittedAny } from "@/core/utils/helpers";
import { USER_ROLES } from "@/core/enums/USER_ROLES";
import { User } from "@/modules/Auth/types";
import { AUTH_EVENTS, authBus } from "@/core/eventBusses/auth";
import updatedSidebarItems from "@/core/Layout/items";
import type { SidebarItem } from "@/types/proxima-design-system";

type RoutePaths = string[];

const extractPaths = <T, U>(items: SidebarItem<T, U>[]): RoutePaths => {
  return items
    .flatMap((item) => [
      item.path || "",
      ...(item.children?.map((child) => child.path || "") || []),
    ])
    .filter(Boolean);
};

const isPathValid = (path: string, validPaths: RoutePaths): boolean => {
  return validPaths.some(
    (validPath) => path.startsWith(validPath) && validPath.length > 1,
  );
};

export function AuthMiddleware(
  to: RouteLocationNormalized,
  _: RouteLocationNormalized,
): boolean | RouteLocationRaw | void {
  const accessToken = storageService.getItem<string>(
    STORAGE_KEYS.ACCESS_TOKEN,
    false,
  );
  const refreshToken = storageService.getItem<string>(
    STORAGE_KEYS.REFRESH_TOKEN,
    false,
  );

  const isAuthenticated = Boolean(accessToken && refreshToken);

  const publicPaths: RoutePaths = [
    PATH.FORGOT_PASSWORD,
    PATH.SIGNUP,
    PATH.RESET_PASSWORD,
    PATH.DISABLE_MFA,
  ];
  const hiddenFromSidebarPaths: RoutePaths = [PATH.GROUPS];
  const validPaths: RoutePaths = [
    PATH.ROOT,
    ...extractPaths(updatedSidebarItems),
    ...publicPaths,
    ...hiddenFromSidebarPaths,
  ];

  if (!to.matched.length) {
    return { path: "/404" };
  }

  if (to.meta.isProtected && !isAuthenticated) {
    return { path: PATH.ROOT };
  }

  if (
    to.meta.isProtected === false &&
    isAuthenticated &&
    to.meta.allowAuthenticated !== true
  ) {
    return { path: PATH.DASHBOARD };
  }

  if (!isPathValid(to.path, validPaths) && isAuthenticated) {
    return { path: validPaths[1] };
  }
}

export function CheckRoleMiddleware(
  to: RouteLocationNormalized,
  _: RouteLocationNormalized,
): boolean | void {
  const user = storageService.getItem<User>(STORAGE_KEYS.USER);
  const allowedRoles = (to.meta?.roles as Array<USER_ROLES>) || [];
  const userRole = user?.role_unique_id as USER_ROLES;

  if (isRoleAllowed(userRole, allowedRoles)) {
    return true;
  }

  authBus.emit(AUTH_EVENTS.ROUTE_ON_ERROR_403, to.path);
  return false;
}

export function CheckPermissionsMiddleware(
  to: RouteLocationNormalized,
  _: RouteLocationNormalized,
): boolean | void {
  const user = storageService.getItem<User>(STORAGE_KEYS.USER);
  const userPermissions = storageService.getItem<PERMISSIONS[]>(
    STORAGE_KEYS.PERMISSIONS,
  );

  const allowedPermissions = (to.meta?.permissions as Array<PERMISSIONS>) || [];

  const isAgentUser = user?.role_unique_id === USER_ROLES.AGENT;
  const canAllowPermissions =
    isAgentUser && userPermissions
      ? isUserPermittedAny(allowedPermissions, userPermissions)
      : true;

  if (canAllowPermissions) {
    return true;
  }

  authBus.emit(AUTH_EVENTS.ROUTE_ON_ERROR_403, to.path);
  return false;
}

export function ScrollToTopMiddleware() {
  const appBody = document.querySelector(".m-body");

  if (appBody) {
    appBody.scrollTo({
      top: 0,
      behavior: "auto",
    });
  }
}
